import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { 
  Menu, 
  X, 
  BarChart2, 
  Users, 
  Wallet, 
  Shield, 
  Settings, 
  UserCog,
  LogOut,
  Home,
  ChevronRight,
  Sun,
  Moon,
  Bell,
  Search,
  Zap
} from 'lucide-react';
import { AdminAuthAPI, AdminTokenService } from '../../services/api';

const menuItems = [
  { text: 'Dashboard', icon: <BarChart2 className="w-5 h-5" />, path: '/dashboard' },
  { text: 'Users', icon: <Users className="w-5 h-5" />, path: '/users' },
  { text: 'Wallet', icon: <Wallet className="w-5 h-5" />, path: '/wallet' },
  { text: 'KYC', icon: <Shield className="w-5 h-5" />, path: '/kyc' },
  { text: 'Admins', icon: <UserCog className="w-5 h-5" />, path: '/admins' },
  { text: 'Settings', icon: <Settings className="w-5 h-5" />, path: '/settings' },
];

function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const adminData = AdminTokenService.getAdminData();

  const handleLogout = () => {
    AdminAuthAPI.logout();
  };

  const isActive = (path) => {
    if (path === '/dashboard') {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/dashboard') return 'Dashboard Overview';
    if (path === '/users') return 'User Management';
    if (path === '/wallet') return 'Wallet Management';
    if (path === '/kyc') return 'KYC Verification';
    if (path === '/admins') return 'Admin Management';
    if (path === '/settings') return 'System Settings';
    return 'Dashboard';
  };

  const notifications = [
    { id: 1, text: 'New user registered', time: '5 min ago', read: false, type: 'user' },
    { id: 2, text: 'Deposit of 0.5 BTC received', time: '15 min ago', read: true, type: 'deposit' },
    { id: 3, text: 'KYC verification pending', time: '1 hour ago', read: false, type: 'kyc' },
    { id: 4, text: 'Withdrawal request processed', time: '2 hours ago', read: true, type: 'withdrawal' },
  ];

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div className="flex h-screen bg-deep-black">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black/80 md:hidden backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed inset-y-0 left-0 z-50 w-72 transform md:relative md:translate-x-0 
        transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
      `}>
        <div className="flex flex-col h-full bg-gradient-to-b from-night-blue to-deep-black border-r border-gray-800">
          {/* Logo Section */}
          <div className="p-6 border-b border-gray-800">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-gradient-to-br from-sky-500 to-blue-600 rounded-xl shadow-lg">
                <Zap className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold bg-gradient-to-r from-sky-400 to-blue-500 bg-clip-text text-transparent">
                  Admin Panel
                </h1>
                <p className="text-xs text-charcoal-gray mt-1">Control Center</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
            {menuItems.map((item) => (
              <button
                key={item.text}
                onClick={() => {
                  navigate(item.path);
                  setSidebarOpen(false);
                }}
                className={`
                  group flex items-center justify-between w-full px-4 py-3.5 rounded-xl
                  transition-all duration-300 hover:scale-[1.02] hover:shadow-lg
                  ${isActive(item.path)
                    ? 'bg-gradient-to-r from-sky-500/20 to-blue-600/10 border-l-4 border-sky-500'
                    : 'hover:bg-gray-800/50 border-l-4 border-transparent'
                  }
                `}
              >
                <div className="flex items-center">
                  <div className={`
                    p-2 rounded-lg mr-3 transition-colors
                    ${isActive(item.path)
                      ? 'bg-gradient-to-br from-sky-500 to-blue-600 text-white'
                      : 'bg-gray-800 text-charcoal-gray group-hover:text-sky-400'
                    }
                  `}>
                    {item.icon}
                  </div>
                  <span className={`
                    font-medium transition-colors
                    ${isActive(item.path)
                      ? 'text-white'
                      : 'text-gray-300 group-hover:text-white'
                    }
                  `}>
                    {item.text}
                  </span>
                </div>
                {isActive(item.path) && (
                  <ChevronRight className="w-4 h-4 text-sky-400 animate-pulse" />
                )}
              </button>
            ))}
          </nav>

          {/* User Profile */}
          <div className="p-4 border-t border-gray-800">
            <div className="flex items-center space-x-3 p-3 rounded-xl bg-gray-900/50">
              <div className="relative">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center shadow-lg">
                  <span className="text-white font-bold">
                    {adminData?.username?.charAt(0).toUpperCase() || 'A'}
                  </span>
                </div>
                <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-emerald-500 rounded-full border-2 border-night-blue" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">
                  {adminData?.username || 'Admin'}
                </p>
                <p className="text-xs text-charcoal-gray capitalize truncate">
                  {adminData?.role || 'Administrator'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-col flex-1 overflow-hidden">
        {/* Top Header */}
        <header className="sticky top-0 z-30 bg-night-blue/80 backdrop-blur-sm border-b border-gray-800">
          <div className="px-6 h-16 flex items-center justify-between">
            {/* Left side */}
            <div className="flex items-center space-x-4">
              <button
                className="md:hidden p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="w-6 h-6" />
              </button>
              
              <div className="hidden md:block">
                <h1 className="text-xl font-bold text-white">{getPageTitle()}</h1>
                <div className="flex items-center space-x-2 text-sm text-charcoal-gray">
                  <Home className="w-3 h-3" />
                  <ChevronRight className="w-3 h-3" />
                  <span>{getPageTitle()}</span>
                </div>
              </div>
            </div>

            {/* Right side */}
            <div className="flex items-center space-x-4">
              {/* Search */}
              <div className="hidden md:block relative">
                <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                  <Search className="w-4 h-4 text-charcoal-gray" />
                </div>
                <input
                  type="text"
                  placeholder="Search..."
                  className="pl-10 pr-4 py-2 bg-gray-900/50 border border-gray-800 rounded-xl text-sm text-white placeholder-charcoal-gray focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 w-64"
                />
              </div>

              {/* Theme Toggle */}
              <button
                onClick={() => setDarkMode(!darkMode)}
                className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
              >
                {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>

              {/* Notifications */}
              <div className="relative">
                <button
                  onClick={() => setNotificationsOpen(!notificationsOpen)}
                  className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors relative"
                >
                  <Bell className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-rose-500 text-xs text-white rounded-full flex items-center justify-center">
                      {unreadCount}
                    </span>
                  )}
                </button>

                {/* Notifications Dropdown */}
                {notificationsOpen && (
                  <div className="absolute right-0 mt-2 w-80 bg-night-blue border border-gray-800 rounded-xl shadow-2xl py-2 z-50">
                    <div className="px-4 py-2 border-b border-gray-800">
                      <h3 className="font-bold text-white">Notifications</h3>
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                      {notifications.map((notification) => (
                        <div
                          key={notification.id}
                          className={`px-4 py-3 hover:bg-gray-800/50 transition-colors border-b border-gray-800/50 last:border-0 ${
                            !notification.read ? 'bg-sky-500/5' : ''
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="text-sm text-white">{notification.text}</p>
                              <p className="text-xs text-charcoal-gray mt-1">{notification.time}</p>
                            </div>
                            {!notification.read && (
                              <div className="w-2 h-2 bg-sky-500 rounded-full mt-1" />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="px-4 py-2 border-t border-gray-800">
                      <button className="text-sm text-sky-400 hover:text-sky-300 w-full text-center py-2">
                        View all notifications
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* User Menu */}
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center space-x-3 focus:outline-none group"
                >
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center shadow-lg group-hover:shadow-xl transition-shadow">
                    <span className="text-white font-bold text-sm">
                      {adminData?.username?.charAt(0).toUpperCase() || 'A'}
                    </span>
                  </div>
                </button>

                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-night-blue border border-gray-800 rounded-xl shadow-2xl py-2 z-50">
                    <div className="px-4 py-3 border-b border-gray-800">
                      <p className="text-sm text-white font-medium">
                        {adminData?.username || 'Admin'}
                      </p>
                      <p className="text-xs text-charcoal-gray capitalize mt-1">
                        {adminData?.role || 'Administrator'}
                      </p>
                    </div>
                    
                    <div className="py-1">
                      <button
                        onClick={() => {
                          navigate('/settings');
                          setUserMenuOpen(false);
                        }}
                        className="w-full px-4 py-2.5 text-sm text-gray-300 hover:bg-gray-800/50 hover:text-white flex items-center transition-colors"
                      >
                        <Settings className="w-4 h-4 mr-3" />
                        Settings
                      </button>
                      <button
                        onClick={handleLogout}
                        className="w-full px-4 py-2.5 text-sm text-rose-300 hover:bg-rose-500/10 hover:text-rose-200 flex items-center transition-colors"
                      >
                        <LogOut className="w-4 h-4 mr-3" />
                        Logout
                      </button>
                    </div>
                    
                    <div className="px-4 py-2 border-t border-gray-800">
                      <div className="flex items-center justify-between text-xs text-charcoal-gray">
                        <span>Last login:</span>
                        <span className="text-emerald-400">Today, 14:30</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Main Content Area */}
        <main className="flex-1 overflow-y-auto">
          <div className="p-6">
            <div className="max-w-7xl mx-auto">
              <Outlet />
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="bg-night-blue/50 border-t border-gray-800 py-4 px-6">
          <div className="flex items-center justify-between text-sm text-charcoal-gray">
            <div className="flex items-center space-x-4">
              <span>© {new Date().getFullYear()} Admin Panel</span>
              <span className="w-1 h-1 bg-charcoal-gray rounded-full" />
              <span>v2.0.0</span>
            </div>
            <div className="flex items-center space-x-4">
              <span className="flex items-center">
                <div className="w-2 h-2 bg-emerald-500 rounded-full mr-2 animate-pulse" />
                System Status: <span className="text-emerald-400 ml-1">Operational</span>
              </span>
              <span className="w-1 h-1 bg-charcoal-gray rounded-full" />
              <span>Last sync: {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default Layout;